// web-frontend/src/components/ChatbotModal.jsx
/**
 * 챗봇 모달 컴포넌트 (v6.0.2)
 * 
 * 🔥 v6.0.2 변경사항 (2026-01-24):
 * - 템플릿 리터럴 문법 오류 완전 수정 (navigate, className, key, onKeyPress)
 * 
 * 🔥 v6.0.1 변경사항 (2025-01-23):
 * - 템플릿 리터럴 문법 오류 수정 (navigate, className)
 * 
 * 🔥 v6.0 변경사항 (2026-01-23):
 * - onAction prop 추가: 부모 컴포넌트(BoothMapPage)로 액션 전달
 * - currentPage prop 추가: 현재 페이지 컨텍스트 전달
 * - i18n.changeLanguage() 사용: 페이지 리로드 없이 언어 변경
 * - MessageBubble에 onAction prop 전달
 * - 액션 상수 파일 사용 (CHATBOT_ACTIONS)
 * 
 * 🔥 v5.0 변경사항:
 * - SET_APP_LANG, HIGHLIGHT_COMPANIES 액션 처리 추가
 * 
 * 🔥 v4.9.8 변경사항:
 * - 템플릿 리터럴 문법 오류 완전 수정 (navigate, className)
 * 
 * 🔥 v4.9.7 변경사항:
 * - 게스트 로그인 시 대화삭제 버튼 숨김 처리
 */
import React, { 
  useState, 
  useEffect, 
  useRef, 
  useCallback, 
  useMemo 
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  X, 
  Send, 
  Trash2, 
  Navigation, 
  MessageCircle, 
  ChevronUp, 
  MapPin, 
  ArrowRight, 
  Search, 
  Star,
  ClipboardList,
  Map,
  Globe
} from 'lucide-react';

// 서비스 및 스토어 import
import chatbotService from '../shared/services/chatbotService';
import i18nService from '../shared/services/i18n';
import useAppStore from '../shared/stores/appStore';
import useAuthStore from '../shared/stores/authStore';

// 상수
import { CHATBOT_ACTIONS } from '../shared/constants/chatbotActions';

// 컴포넌트 import
import MessageBubble from './chat/MessageBubble';
import ConfirmModal from './common/ConfirmModal';
import ChatbotMiniMap from './chat/ChatbotMiniMap';

import logger from '../shared/utils/logger';
import './ChatbotModal.css';

/**
 * ChatbotModal 컴포넌트
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - 모달 열림 여부
 * @param {Function} props.onClose - 모달 닫기 핸들러
 * @param {Function} [props.onAction] - 🔥 v6.0: 부모 컴포넌트로 액션 전달 (action, payload) => void
 * @param {string} [props.currentPage] - 🔥 v6.0: 현재 페이지 컨텍스트 (예: 'BoothMap', 'Companies')
 */
const ChatbotModal = ({ 
  isOpen, 
  onClose, 
  onAction,      // 🔥 v6.0: 부모 컴포넌트로 액션 전달
  currentPage    // 🔥 v6.0: 현재 페이지 컨텍스트
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage } = useAppStore();
  const { isGuest } = useAuthStore();
  
  // ========================================
  // 메시지 상태
  // ========================================
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  // [2026-07-02] 대화 초기화 window.confirm → ConfirmModal (FABLE P1-10)
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  
  // ========================================
  // 타이핑/스트리밍 상태
  // ========================================
  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  
  // ========================================
  // UI 상태
  // ========================================
  const [currentSuggestions, setCurrentSuggestions] = useState([]);
  const [relatedCompanies, setRelatedCompanies] = useState([]);
  const [isCompanyListExpanded, setIsCompanyListExpanded] = useState(true);
  
  // ========================================
  // 🔥 네비게이션 상태
  // ========================================
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [locationInputMode, setLocationInputMode] = useState(false);
  const [currentLocationInput, setCurrentLocationInput] = useState('');
  
  // ========================================
  // 🔥 v5.0: HIGHLIGHT_COMPANIES 상태
  // ========================================
  const [highlightedBooths, setHighlightedBooths] = useState([]);
  const [selectedBooth, setSelectedBooth] = useState(null);
  
  // ========================================
  // Refs
  // ========================================
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  // 🔥 스트리밍 메시지 ID 및 타임스탬프 (렌더링 최적화용)
  const streamingMessageIdRef = useRef(null);
  const streamingTimestampRef = useRef(null);
  
  // ========================================
  // 스크롤 처리
  // ========================================
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);
  
  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage, isTyping, relatedCompanies, isCompanyListExpanded, locationInputMode, scrollToBottom]);
  
  // ========================================
  // 컴포넌트 언마운트 시 정리
  // ========================================
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);
  
  // ========================================
  // 초기화
  // ========================================
  const updateUiTexts = useCallback(() => {
    const newSuggestions = [
      i18nService.t('chatbot.q1') || '화장실이 어디인가요?',
      i18nService.t('chatbot.q2') || '드론 기업 추천해줘',
      i18nService.t('chatbot.q3') || '내 관심분야 기업 추천해줘',
    ];
    setCurrentSuggestions(newSuggestions);
  }, []);
  
  const initializeChat = useCallback(async () => {
    await chatbotService.initialize();
    const history = chatbotService.getLocalMessageHistory();
    
    if (history.length > 0) {
      setMessages(history);
    } else {
      setMessages([{
        id: 'welcome',
        type: 'bot',
        role: 'assistant',
        content: i18nService.t('chatbot.welcome') || '안녕하세요! DSK AI 이월이입니다. 🚁\n\n무엇을 도와드릴까요?',
        timestamp: new Date().toISOString()
      }]);
    }
  }, []);
  
  useEffect(() => {
    if (isOpen) {
      updateUiTexts();
      if (messages.length === 0) {
        initializeChat();
      }
    }
  }, [isOpen, language, messages.length, updateUiTexts, initializeChat]);
  
  // ========================================
  // 메시지 ID 생성
  // ========================================
  const generateMessageId = useCallback(() => {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);
  
  // ========================================
  // 🔥 스트리밍 메시지 객체 (메모이제이션)
  // ========================================
  const streamingMessageObject = useMemo(() => {
    if (!isStreaming || !streamingMessage) return null;
    
    return {
      id: streamingMessageIdRef.current || 'streaming',
      type: 'bot',
      role: 'assistant',
      content: streamingMessage,
      timestamp: streamingTimestampRef.current || new Date().toISOString(),
      isStreaming: true
    };
  }, [isStreaming, streamingMessage]);
  
  // ========================================
  // 🔥 v6.0: 부모 컴포넌트로 액션 전달
  // ========================================
  const dispatchToParent = useCallback((action, payload) => {
    if (onAction) {
      logger.debug('📤 부모 컴포넌트로 액션 전달:', action, payload);
      onAction(action, payload);
    }
  }, [onAction]);
  
  // ========================================
  // 🔥 v6.0: SET_APP_LANG 액션 처리 (i18n.changeLanguage 사용)
  // ========================================
  const handleSetAppLang = useCallback((targetLang) => {
    logger.debug('🌐 언어 변경 요청:', targetLang);
    
    // 🔥 v6.0: i18n.changeLanguage() 사용 (페이지 리로드 없음)
    if (i18nService.changeLanguage) {
      i18nService.changeLanguage(targetLang);
    } else if (i18nService.i18n?.changeLanguage) {
      // i18next 직접 접근
      i18nService.i18n.changeLanguage(targetLang);
    }
    
    // localStorage 업데이트
    localStorage.setItem('i18nextLng', targetLang);
    localStorage.setItem('user_preferred_language', targetLang);
    
    // appStore 언어 상태 업데이트
    if (setLanguage) {
      setLanguage(targetLang);
    }
    
    // UI 텍스트 갱신
    updateUiTexts();
    
    // 🔥 v6.0: 부모 컴포넌트에 알림
    dispatchToParent(CHATBOT_ACTIONS.SET_APP_LANG, { language: targetLang });
    
    // 사용자에게 피드백 메시지
    const langNames = {
      ko: '한국어',
      en: 'English',
      ja: '日本語',
      zh: '中文'
    };
    const feedbackMsg = {
      id: generateMessageId(),
      type: 'bot',
      role: 'assistant',
      content: `✅ 언어가 ${langNames[targetLang] || targetLang}(으)로 변경되었습니다.`,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, feedbackMsg]);
    
  }, [setLanguage, updateUiTexts, dispatchToParent, generateMessageId]);
  
  // ========================================
  // 🔥 v5.0: HIGHLIGHT_COMPANIES 액션 처리 함수
  // ========================================
  const handleHighlightCompanies = useCallback((booths) => {
    logger.debug('🗺️ 관심분야 기업 하이라이트:', booths);
    
    // 하이라이트할 부스 설정
    setHighlightedBooths(booths);
    
    // 첫 번째 부스 선택 (필요 시 카메라 이동용)
    if (booths && booths.length > 0) {
      setSelectedBooth(booths[0]);
    }
    
    // 🔥 v6.0: 부모 컴포넌트에 알림
    dispatchToParent(CHATBOT_ACTIONS.HIGHLIGHT_COMPANIES, { booths });
  }, [dispatchToParent]);
  
  // ========================================
  // 🔥 v5.0: 하이라이트된 기업 지도로 이동
  // ========================================
  const handleViewHighlightedOnMap = useCallback(() => {
    logger.debug('🗺️ 하이라이트된 부스 지도에서 보기:', highlightedBooths);
    
    onClose();
    navigate('/map', { 
      state: { 
        highlightedBooths: highlightedBooths,
        selectedBooth: selectedBooth
      } 
    });
  }, [navigate, onClose, highlightedBooths, selectedBooth]);
  
  // ========================================
  // 🔥 네비게이션 핸들러 - pathPoints도 전달
  // ========================================
  const handleNavigation = useCallback((from, to, pathPoints) => {
    logger.debug('🚀 handleNavigation 호출:', { from, to, pathPoints });
    onClose();
    navigate('/map', { 
      state: { 
        from, 
        to,
        pathPoints
      } 
    });
  }, [navigate, onClose]);
  
  // ========================================
  // 🔥 설문조사 페이지 이동
  // ========================================
  const handleGoToSurvey = useCallback(() => {
    logger.debug('📋 설문조사 바로가기 클릭');
    onClose();
    navigate('/survey');
  }, [navigate, onClose]);
  
  // 🔥 MessageBubble에서 사용할 설문조사 클릭 핸들러 (별칭)
  const handleSurveyClick = useCallback(() => {
    logger.debug('📋 설문조사 바로가기 클릭 (MessageBubble)');
    
    if (onClose) {
      onClose();
    }
    
    navigate('/survey');
  }, [onClose, navigate]);
  
  // ========================================
  // 🔥 기업 클릭 핸들러
  // 🔥 v6.0.2: 템플릿 리터럴 문법 수정
  // ========================================
  const handleCompanyClick = useCallback((company) => {
    if (company && company.id) {
      navigate(`/companies/${company.id}`);
      onClose();
    }
  }, [navigate, onClose]);
  
  // ========================================
  // 🔥 v4.9.5: 설문 버튼 표시 여부 판단 함수
  // ========================================
  const shouldShowSurveyPromptButton = useCallback((msg) => {
    if (msg.type !== 'bot' || msg.isStreaming) return false;
    if (msg.action === 'SURVEY_REQUIRED' || msg.action === 'NAVIGATE') return false;
    if (msg.action === 'SURVEY_PROMPT') return true;
    if (msg.content && msg.content.includes('설문조사를 완료하시면')) {
      return true;
    }
    return false;
  }, []);
  
  // ========================================
  // 🔥 v6.0: MessageBubble에서 호출하는 통합 액션 핸들러
  // 🔥 v6.0.2: 템플릿 리터럴 문법 수정
  // ========================================
  const handleMessageBubbleAction = useCallback((action, payload) => {
    logger.debug('🎬 MessageBubble 액션:', action, payload);
    
    switch (action) {
      case CHATBOT_ACTIONS.SET_APP_LANG:
        if (payload?.language) {
          handleSetAppLang(payload.language);
        }
        break;
        
      case CHATBOT_ACTIONS.OPEN_MEETING_MODAL:
        if (payload?.companyId) {
          logger.debug('📅 미팅 예약 요청:', payload);
          // 부모 컴포넌트에 전달
          dispatchToParent(action, payload);
          // 기업 상세 페이지로 이동 (미팅 탭)
          navigate(`/companies/${payload.companyId}?tab=meeting`);
          onClose();
        }
        break;
        
      case CHATBOT_ACTIONS.CLOSE_POPUP:
        // 언어 변경 거절 시 별도 처리 없음
        logger.debug('언어 변경 거절됨');
        break;
        
      default:
        // 알 수 없는 액션은 부모에게 전달
        dispatchToParent(action, payload);
    }
  }, [handleSetAppLang, dispatchToParent, navigate, onClose]);
  
  // ========================================
  // 🔥 v5.0: 액션 처리 핸들러 (통합)
  // ========================================
  const handleBotAction = useCallback((action, actionPayload) => {
    logger.debug('🎬 봇 액션 처리:', action, actionPayload);
    
    switch (action) {
      case CHATBOT_ACTIONS.SET_APP_LANG:
        if (actionPayload?.target_lang) {
          handleSetAppLang(actionPayload.target_lang);
        }
        break;
        
      case CHATBOT_ACTIONS.HIGHLIGHT_COMPANIES:
      case CHATBOT_ACTIONS.SHOW_COMPANIES_ON_MAP:
        if (actionPayload?.booths && actionPayload.booths.length > 0) {
          handleHighlightCompanies(actionPayload.booths);
        }
        break;
        
      case CHATBOT_ACTIONS.ASK_LOCATION:
        const destinationBooth = actionPayload?.to || actionPayload?.destination;
        const destinationCompany = actionPayload?.destination_company;
        const fType = actionPayload?.facility_type; // 🆕 백엔드에서 보낸 시설 타입 수신
        
        logger.debug('🧭 네비게이션 설정:', { destinationBooth, destinationCompany, facilityType: fType });
        
        setPendingNavigation({ 
          destination: destinationBooth,
          company: destinationCompany,
          facility_type: fType // 🆕 상태에 저장
        });
        setLocationInputMode(true);
        break;
        
      case CHATBOT_ACTIONS.SHOW_OPTIMAL_ROUTE:
        // 🔥 v6.0: 다중 목적지 최적 경로
        if (actionPayload?.route) {
          dispatchToParent(action, actionPayload);
          // 지도 페이지로 이동
          onClose();
          navigate('/map', { 
            state: { 
              multiRoute: actionPayload.route,
              destinations: actionPayload.destinations
            } 
          });
        }
        break;
        
      case CHATBOT_ACTIONS.SHOW_CONGESTION:
        // 🔥 v6.0: 혼잡도 데이터
        if (actionPayload?.congestion) {
          dispatchToParent(action, actionPayload);
        }
        break;
        
      case CHATBOT_ACTIONS.SHOW_FACILITY: // 🆕 v5.6: 화장실/시설 찾기 액션
        logger.debug('🚽 시설 안내 데이터 수신:', actionPayload);
        // 부모 컴포넌트(BoothMapPage)에도 시설 데이터 전달
        dispatchToParent(action, actionPayload);
        break;
        
      default:
        // 다른 액션은 부모 컴포넌트로 전달
        dispatchToParent(action, actionPayload);
    }
  }, [handleSetAppLang, handleHighlightCompanies, dispatchToParent, onClose, navigate]);
  
  // ========================================
  // 🤖 Phase 1C: 피드백 핸들러
  // ========================================
  const [feedbackGiven, setFeedbackGiven] = useState(new Set());

  const handleFeedback = useCallback(async (messageId, isPositive) => {
    if (feedbackGiven.has(messageId)) return;
    try {
      await chatbotService.submitFeedback(
        chatbotService.conversationId,
        isPositive ? 5 : 1,
        JSON.stringify({ message_id: messageId })
      );
      setFeedbackGiven(prev => new Set(prev).add(messageId));
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, feedbackGiven: isPositive ? 'up' : 'down' } : m
      ));
    } catch (err) {
      logger.error('❌ 피드백 전송 실패:', err);
    }
  }, [feedbackGiven]);

  // ========================================
  // 🔥 메시지 전송 핸들러 (v6.0 currentPage 컨텍스트 추가)
  // ========================================
  const handleSendMessage = useCallback(async (text = null) => {
    const messageText = text || inputText;
    if (!messageText.trim() || isTyping || isStreaming) return;
    
    // 🔥 v6.0: 현재 페이지 컨텍스트 추출
    const currentContext = {
      current_page: currentPage || location.pathname,
      page_params: location.search
    };
    
    if (location.pathname.startsWith('/companies/')) {
      currentContext.viewing_company_id = location.pathname.split('/')[2];
    } else if (location.pathname.startsWith('/events/')) {
      currentContext.viewing_event_id = location.pathname.split('/')[2];
    }
    
    logger.debug('📡 전송 컨텍스트:', currentContext);
    
    const userMsg = {
      id: generateMessageId(),
      type: 'user',
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsTyping(true);
    setRelatedCompanies([]);
    setHighlightedBooths([]);  // 🔥 v5.0: 이전 하이라이트 초기화
    
    streamingMessageIdRef.current = generateMessageId();
    streamingTimestampRef.current = new Date().toISOString();
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    let accumulatedMessage = '';
    
    try {
      await chatbotService.sendMessageStreaming(
        messageText.trim(),
        
        // onChunk
        (chunk) => {
          setIsTyping(false);
          setIsStreaming(true);
          
          if (chunk && chunk.content) {
            if (chunk.accumulated) {
              accumulatedMessage = chunk.accumulated;
              setStreamingMessage(chunk.accumulated);
            } else {
              accumulatedMessage += chunk.content;
              setStreamingMessage(accumulatedMessage);
            }
          }
          
          if (chunk && chunk.companies && chunk.companies.length > 0) {
            setRelatedCompanies(chunk.companies);
          }
        },
        
        // onComplete
        (data) => {
          logger.debug('✅ 답변 완료:', data);
          
          const finalContent = data.full_response || accumulatedMessage || '';
          
          const botMsg = {
            id: streamingMessageIdRef.current || generateMessageId(),
            type: 'bot',
            role: 'assistant',
            content: finalContent,
            timestamp: streamingTimestampRef.current || new Date().toISOString(),
            action: data.action,
            action_payload: data.action_payload,  // 🔥 v6.0: snake_case 유지
            actionPayload: data.action_payload,   // 레거시 호환
            companies: data.companies || data.related_companies,
            related_companies: data.related_companies  // 🔥 v6.0: snake_case 유지
          };
          
          setMessages(prev => [...prev, botMsg]);
          
          setStreamingMessage('');
          setIsStreaming(false);
          setIsTyping(false);
          
          // 🔥 v5.0: 통합 액션 처리
          if (data.action) {
            handleBotAction(data.action, data.action_payload);
          }
          
          if (data.suggestions?.length > 0) {
            setCurrentSuggestions(data.suggestions.slice(0, 3));
          }
          
          if (data.related_companies?.length > 0) {
            setRelatedCompanies(data.related_companies);
            setIsCompanyListExpanded(true);
          }
          
          streamingMessageIdRef.current = null;
          streamingTimestampRef.current = null;
        },
        
        // onError
        (error) => {
          logger.error('❌ 스트리밍 오류:', error);
          
          setIsTyping(false);
          setIsStreaming(false);
          setStreamingMessage('');
          
          setMessages(prev => [...prev, {
            id: generateMessageId(),
            type: 'bot',
            role: 'assistant',
            content: i18nService.t('chatbot.error') || '죄송합니다. 오류가 발생했습니다.',
            timestamp: new Date().toISOString(),
            isError: true
          }]);
          
          streamingMessageIdRef.current = null;
          streamingTimestampRef.current = null;
        },
        
        currentContext,
        abortControllerRef.current?.signal
      );
      
    } catch (error) {
      logger.error('❌ 메시지 전송 실패:', error);
      
      if (error.name !== 'AbortError') {
        setIsTyping(false);
        setIsStreaming(false);
        setStreamingMessage('');
      }
    }
  }, [inputText, isTyping, isStreaming, generateMessageId, location.pathname, location.search, currentPage, handleBotAction]);

  // ========================================
  // 🤖 Phase 1B: 재시도 핸들러
  // ========================================
  const handleRetry = useCallback((failedText) => {
    setMessages(prev => prev.filter(m => !(m.isError && m.content === failedText)));
    handleSendMessage(failedText);
  }, [handleSendMessage]);

  // ========================================
  // 🔥 부스 번호 입력 후 경로 요청
  // ========================================
  const handleLocationSubmit = useCallback(async () => {
    const from = currentLocationInput.trim().toUpperCase();
    const to = pendingNavigation?.destination;
    const facilityType = pendingNavigation?.facility_type; // 🆕 시설 타입 추가
    
    // ✅ 수정: 목적지 부스(to)가 없더라도 시설 타입(facilityType)이 있으면 통과
    if (!from || (!to && !facilityType)) {
      logger.warn('⚠️ 입력값 또는 목적지 정보 부족:', { 
        input: from, 
        pending: pendingNavigation 
      });
      return;
    }
    
    const companyName = pendingNavigation?.company;
    
    logger.debug('🧭 경로 요청 준비:', { from, to, facilityType, companyName });
    
    // ✅ 시설물 질문인 경우와 일반 경로 질문인 경우를 구분하여 메시지 생성
    const navMessage = facilityType 
      ? `${from}에서 가장 가까운 ${facilityType} 안내해줘`
      : `${from}에서 ${to}까지 경로 안내해줘`;
    
    const userMsg = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'user',
      role: 'user',
      content: navMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMsg]);
    
    setLocationInputMode(false);
    setCurrentLocationInput('');
    setPendingNavigation(null);
    setIsTyping(true);
    setRelatedCompanies([]);
    
    const streamingId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const streamingTimestamp = new Date().toISOString();
    streamingMessageIdRef.current = streamingId;
    streamingTimestampRef.current = streamingTimestamp;
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    let accumulatedMessage = '';
    
    try {
      await chatbotService.sendMessageStreaming(
        navMessage,
        
        // onChunk
        (chunk) => {
          setIsTyping(false);
          setIsStreaming(true);
          
          if (chunk && chunk.content) {
            if (chunk.accumulated) {
              accumulatedMessage = chunk.accumulated;
              setStreamingMessage(chunk.accumulated);
            } else {
              accumulatedMessage += chunk.content;
              setStreamingMessage(accumulatedMessage);
            }
          }
        },
        
        // onComplete
        (data) => {
          logger.debug('✅ 경로 응답 완료:', data);
          
          const finalContent = data.full_response || accumulatedMessage || '';
          
          const botMsg = {
            id: streamingId,
            type: 'bot',
            role: 'assistant',
            content: finalContent,
            timestamp: streamingTimestamp,
            action: data.action,
            action_payload: data.action_payload,
            actionPayload: data.action_payload,
            companies: data.companies || data.related_companies,
            related_companies: data.related_companies
          };
          
          setMessages(prev => [...prev, botMsg]);
          
          setStreamingMessage('');
          setIsStreaming(false);
          setIsTyping(false);
          
          // 🔥 v5.0: 통합 액션 처리
          if (data.action) {
            handleBotAction(data.action, data.action_payload);
          }
          
          if (data.suggestions?.length > 0) {
            setCurrentSuggestions(data.suggestions.slice(0, 3));
          }
          
          streamingMessageIdRef.current = null;
          streamingTimestampRef.current = null;
        },
        
        // onError
        (error) => {
          logger.error('❌ 경로 요청 오류:', error);
          
          setIsTyping(false);
          setIsStreaming(false);
          setStreamingMessage('');
          
          setMessages(prev => [...prev, {
            id: `msg_${Date.now()}`,
            type: 'bot',
            role: 'assistant',
            content: i18nService.t('chatbot.nav_error') || '죄송합니다. 경로 안내 중 오류가 발생했습니다.',
            timestamp: new Date().toISOString(),
            isError: true
          }]);
          
          streamingMessageIdRef.current = null;
          streamingTimestampRef.current = null;
        },
        
        {},
        abortControllerRef.current?.signal
      );
      
    } catch (error) {
      logger.error('❌ 경로 요청 실패:', error);
      
      if (error.name !== 'AbortError') {
        setIsTyping(false);
        setIsStreaming(false);
        setStreamingMessage('');
      }
    }
  }, [currentLocationInput, pendingNavigation, handleBotAction]);
  
  // ========================================
  // 대화 초기화
  // ========================================
  const handleClearChat = useCallback(() => {
    // [2026-07-02] window.confirm → ConfirmModal (FABLE P1-10)
    setClearConfirmOpen(true);
  }, []);

  const executeClearChat = useCallback(async () => {
    setClearConfirmOpen(false);
    const resetMsg = i18nService.t('chatbot.resetMessage') || '대화가 초기화되었습니다. 새로운 질문을 해주세요! 😊';

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    await chatbotService.deleteConversation();
    setMessages([{
      id: generateMessageId(),
      type: 'bot',
      role: 'assistant',
      content: resetMsg,
      timestamp: new Date().toISOString()
    }]);
    setRelatedCompanies([]);
    setPendingNavigation(null);
    setLocationInputMode(false);
    setIsStreaming(false);
    setStreamingMessage('');
    setIsTyping(false);
    setHighlightedBooths([]);  // 🔥 v5.0: 하이라이트 초기화
    setSelectedBooth(null);
    updateUiTexts();
  }, [generateMessageId, updateUiTexts]);
  
  // ========================================
  // 키보드 이벤트 핸들러
  // ========================================
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);
  
  // 🔥 v6.0.2: 위치 입력 키보드 핸들러
  const handleLocationKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLocationSubmit();
    }
  }, [handleLocationSubmit]);
  
  // ========================================
  // 렌더링: 닫힌 상태
  // ========================================
  if (!isOpen) return null;
  
  // ========================================
  // 🔥 Action 버튼 스타일
  // ========================================
  const actionBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    backgroundColor: 'var(--color-bg-card, #1E293B)',
    color: 'var(--color-primary-light, var(--color-primary, #1A3A5E))',
    border: '1px solid var(--color-primary-light, var(--color-primary, #1A3A5E))',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginBottom: '8px'
  };
  
  const surveyBtnStyle = {
    ...actionBtnStyle,
    color: '#10B981',
    border: '1px solid #10B981',
  };
  
  const highlightMapBtnStyle = {
    ...actionBtnStyle,
    color: '#F59E0B',
    border: '1px solid #F59E0B',
  };
  
  // ========================================
  // 렌더링: 전체 모달
  // ========================================
  return (
    <div className="chatbot-overlay">
      <div className="chatbot-window">
        
        {/* ==================== Header ==================== */}
        <div className="chatbot-header">
          <div className="header-title">
            <MessageCircle size={20} className="header-icon" />
            <div>
              <h3>{i18nService.t('chatbot.title') || 'DSK AI 이월이'}</h3>
              <span className="status-text">
                {isStreaming ? (i18nService.t('chatbot.responding') || '응답 중...') : (i18nService.t('chatbot.subtitle') || '온라인')}
              </span>
            </div>
          </div>
          <div className="header-actions">
            {/* 🔥 v4.9.7: 게스트가 아닐 때만 대화삭제 버튼 표시 */}
            {!isGuest && (
              <button 
                onClick={handleClearChat} 
                title={i18nService.t('chatbot.clear') || '대화 초기화'}
                disabled={isTyping || isStreaming}
              >
                <Trash2 size={18} />
              </button>
            )}
            <button onClick={onClose} title={i18nService.t('chatbot.close') || '닫기'}>
              <X size={24} />
            </button>
          </div>
        </div>
        
        {/* ==================== Messages Area ==================== */}
        <div className="chatbot-messages">
          {messages.map((msg, idx) => (
            <div key={msg.id || `msg-${idx}`} style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <MessageBubble
                message={msg}
                isMyMessage={msg.type === 'user'}
                onSurveyClick={handleSurveyClick}
                onCompanyClick={handleCompanyClick}
                onAction={handleMessageBubbleAction}
                onRetry={msg.isError ? handleRetry : undefined}
                onFeedback={msg.type === 'bot' ? handleFeedback : undefined}
              />
              
              {/* 🔥 v4.9: 네비게이션 미니맵 + pathPoints 전달 */}
              {msg.action === 'NAVIGATE' && (msg.action_payload || msg.actionPayload) && (() => {
                const payload = msg.action_payload || msg.actionPayload;
                // ✅ 필드명 수정: from_booth/to_booth 우선, from/to fallback
                const fromBooth = payload.from_booth || payload.from;
                const toBooth = payload.to_booth || payload.to;
                const pathPoints = payload.path || payload.path_points;
                return (
                  <div 
                    className="bot-action-container" 
                    style={{ 
                      alignSelf: 'flex-start', 
                      marginLeft: '12px', 
                      marginTop: '8px',
                      width: 'calc(100% - 24px)',
                      maxWidth: '320px',
                      cursor: 'pointer'
                    }}
                  >
                    <ChatbotMiniMap
                      fromBooth={fromBooth}
                      toBooth={toBooth}
                      pathPoints={pathPoints}
                      onClick={() => {
                        logger.debug('🗺️ 네비게이션 미니맵 클릭됨:', { fromBooth, toBooth, pathPoints });
                        handleNavigation(fromBooth, toBooth, pathPoints);
                      }}
                    />
                  </div>
                );
              })()}
              
              {/* 🆕 v5.6: SHOW_FACILITY 액션 - 시설(화장실 등) 위치 미니맵 */}
              {msg.action === 'SHOW_FACILITY' && (msg.action_payload || msg.actionPayload) && (() => {
                const payload = msg.action_payload || msg.actionPayload;
                // 🔥 facilities 배열이 있으면 첫 번째 시설의 booth_number를 목적지로 사용
                const facilityBooth = payload.facilities?.[0]?.booth_number;
                // ✅ 필드명 수정: from_booth/to_booth 우선, from/to fallback
                const toBooth = payload.to_booth || payload.to || facilityBooth;
                const fromBooth = payload.from_booth || payload.from;
                const pathPoints = payload.path || payload.path_points;
                return (
                  <div 
                    className="bot-action-container" 
                    style={{ 
                      alignSelf: 'flex-start', 
                      marginLeft: '12px', 
                      marginTop: '8px',
                      width: 'calc(100% - 24px)',
                      maxWidth: '320px',
                      cursor: 'pointer' // ✅ 클릭 가능함을 시각적으로 표시
                    }}
                  >
                    <ChatbotMiniMap
                      fromBooth={fromBooth}
                      toBooth={toBooth}
                      pathPoints={pathPoints}
                      facilityType={payload.facility_type}
                      facilities={payload.facilities}
                      onClick={() => {
                        logger.debug('🗺️ 시설 미니맵 클릭됨, 이동 시작:', payload);
                        if (toBooth) {
                          handleNavigation(fromBooth, toBooth, pathPoints);
                        }
                      }}
                    />
                  </div>
                );
              })()}
              
              {/* 🔥 v5.0: HIGHLIGHT_COMPANIES 액션 - 지도에서 보기 버튼 */}
              {msg.action === 'HIGHLIGHT_COMPANIES' && (msg.action_payload || msg.actionPayload)?.booths?.length > 0 && (
                <div 
                  className="bot-action-container" 
                  style={{ alignSelf: 'flex-start', marginLeft: '12px', marginTop: '8px' }}
                >
                  <button 
                    className="action-highlight-btn"
                    style={highlightMapBtnStyle}
                    onMouseEnter={(e) => { 
                      e.currentTarget.style.backgroundColor = '#F59E0B'; 
                      e.currentTarget.style.color = 'white'; 
                    }}
                    onMouseLeave={(e) => { 
                      e.currentTarget.style.backgroundColor = '#252525'; 
                      e.currentTarget.style.color = '#F59E0B'; 
                    }}
                    onClick={handleViewHighlightedOnMap}
                  >
                    <Map size={14} />
                    <span>지도에서 {(msg.action_payload || msg.actionPayload).booths.length}개 부스 보기</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              )}
              
              {/* 🔥 v5.0: SET_APP_LANG 액션 - 언어 변경 버튼 */}
              {msg.action === 'SET_APP_LANG' && (msg.action_payload || msg.actionPayload)?.target_lang && (
                <div 
                  className="bot-action-container" 
                  style={{ alignSelf: 'flex-start', marginLeft: '12px', marginTop: '8px' }}
                >
                  <button 
                    className="action-lang-btn"
                    style={{
                      ...actionBtnStyle,
                      color: '#6366F1',
                      border: '1px solid #6366F1',
                    }}
                    onMouseEnter={(e) => { 
                      e.currentTarget.style.backgroundColor = '#6366F1'; 
                      e.currentTarget.style.color = 'white'; 
                    }}
                    onMouseLeave={(e) => { 
                      e.currentTarget.style.backgroundColor = '#252525'; 
                      e.currentTarget.style.color = '#6366F1'; 
                    }}
                    onClick={() => handleSetAppLang((msg.action_payload || msg.actionPayload).target_lang)}
                  >
                    <Globe size={14} />
                    <span>
                      {i18nService.t(`chatbot.lang_change_${(msg.action_payload || msg.actionPayload).target_lang}`) ||
                        `Change to ${(msg.action_payload || msg.actionPayload).target_lang}`}
                    </span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              )}
              
              {/* 🔥 설문조사 필요 액션 버튼 */}
              {msg.action === 'SURVEY_REQUIRED' && (
                <div 
                  className="bot-action-container" 
                  style={{ alignSelf: 'flex-start', marginLeft: '12px', marginTop: '8px' }}
                >
                  <button 
                    className="action-survey-btn"
                    style={surveyBtnStyle}
                    onMouseEnter={(e) => { 
                      e.currentTarget.style.backgroundColor = '#10B981'; 
                      e.currentTarget.style.color = 'white'; 
                    }}
                    onMouseLeave={(e) => { 
                      e.currentTarget.style.backgroundColor = '#252525'; 
                      e.currentTarget.style.color = '#10B981'; 
                    }}
                    onClick={handleGoToSurvey}
                  >
                    <ClipboardList size={14} />
                    <span>설문조사 참여하기</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              )}
              
              {/* 🔥 v4.9.5: 설문조사 유도 버튼 (텍스트 기반 감지 포함) */}
              {shouldShowSurveyPromptButton(msg) && (
                <div 
                  className="bot-action-container" 
                  style={{ alignSelf: 'flex-start', marginLeft: '12px', marginTop: '8px' }}
                >
                  <button 
                    className="action-survey-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '10px 16px',
                      backgroundColor: '#252525',
                      color: '#8B5CF6',
                      border: '1px solid #8B5CF6',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => { 
                      e.currentTarget.style.backgroundColor = '#8B5CF6'; 
                      e.currentTarget.style.color = 'white'; 
                    }}
                    onMouseLeave={(e) => { 
                      e.currentTarget.style.backgroundColor = '#252525'; 
                      e.currentTarget.style.color = '#8B5CF6'; 
                    }}
                    onClick={handleGoToSurvey}
                  >
                    <ClipboardList size={14} />
                    <span>맞춤 기업 추천받기 (설문 참여)</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              )}
              
              {/* 🔥 Phase 2C: 기업 추천/검색 결과 인라인 카드 */}
              {(msg.action === 'RECOMMEND_COMPANIES' || msg.action === 'SEARCH_RESULT') &&
                (msg.action_payload || msg.actionPayload)?.companies?.length > 0 && (
                <div className="inline-companies" style={{ marginLeft: '12px', marginTop: '8px' }}>
                  {((msg.action_payload || msg.actionPayload).companies || []).slice(0, 5).map((comp, ci) => (
                    <div
                      key={ci}
                      className="chat-company-card"
                      onClick={() => handleCompanyClick(comp)}
                    >
                      <div className="comp-info">
                        <div className="comp-name">{comp.company_name}</div>
                        <div className="comp-booth">
                          {i18nService.t('chatbot.booth') || '부스'} {comp.booth_number}
                        </div>
                      </div>
                      <div className="comp-action">
                        <Navigation size={14} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          
          {/* 🔥 현재 위치 입력 모드 */}
          {locationInputMode && (
            <div 
              className="location-input-container" 
              style={{
                padding: '12px',
                backgroundColor: '#f0f9ff',
                borderRadius: '12px',
                margin: '8px 12px',
                border: '1px solid #1A3A5E'
              }}
            >
              <p style={{ fontSize: '13px', color: '#1e40af', marginBottom: '8px' }}>
                📍 현재 계신 부스 번호를 입력해주세요
                {pendingNavigation?.company && pendingNavigation?.destination && (
                  <span style={{ display: 'block', marginTop: '4px', fontWeight: '600' }}>
                    → 목적지: {pendingNavigation.company} ({pendingNavigation.destination})
                  </span>
                )}
                {pendingNavigation?.facility_type && !pendingNavigation?.destination && (
                  <span style={{ display: 'block', marginTop: '4px', fontWeight: '600' }}>
                    → 가장 가까운 {pendingNavigation.facility_type}을(를) 찾아드립니다
                  </span>
                )}
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="예: A01, B15, C03"
                  value={currentLocationInput}
                  onChange={(e) => setCurrentLocationInput(e.target.value.toUpperCase())}
                  onKeyPress={handleLocationKeyPress}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #93c5fd',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
                <button
                  onClick={handleLocationSubmit}
                  disabled={!currentLocationInput.trim()}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: currentLocationInput.trim() ? 'var(--color-primary, #1A3A5E)' : '#d1d5db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: currentLocationInput.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}
                >
                  확인
                </button>
              </div>
            </div>
          )}
          
          {/* 타이핑 인디케이터 */}
          {isTyping && !isStreaming && !streamingMessage && (
            <MessageBubble 
              message={{ 
                id: 'typing-indicator', 
                type: 'bot', 
                content: '', 
                timestamp: new Date().toISOString(),
                isTyping: true 
              }} 
              isMyMessage={false} 
            />
          )}
          
          {/* 🔥 스트리밍 메시지 */}
          {isStreaming && streamingMessageObject && (
            <MessageBubble
              key={streamingMessageObject.id}
              message={streamingMessageObject}
              isMyMessage={false}
            />
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* ==================== Suggestions ==================== */}
        {!isTyping && !isStreaming && !locationInputMode && currentSuggestions.length > 0 && (
          <div className="chatbot-suggestions">
            <span className="suggestion-label">
              {i18nService.t('chatbot.suggestions_label') || '추천 질문'}
            </span>
            <div className="suggestion-chips">
              {currentSuggestions.map((sug, idx) => (
                <button 
                  key={`suggestion-${idx}`}
                  onClick={() => {
                    if (sug.includes('설문조사')) {
                      handleGoToSurvey();
                    } else {
                      handleSendMessage(sug);
                    }
                  }}
                >
                  {sug}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* ==================== 🔥 v5.0: Highlighted Booths (관심분야 기업) ==================== */}
        {!isTyping && !isStreaming && !locationInputMode && highlightedBooths.length > 0 && (
          <div 
            className="chatbot-highlighted-booths"
            style={{
              padding: '8px 12px',
              backgroundColor: '#FEF3C7',
              borderTop: '1px solid #F59E0B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <MapPin size={14} style={{ color: '#D97706' }} />
              <span style={{ fontSize: '12px', color: '#92400E' }}>
                관심분야 부스 {highlightedBooths.length}개 표시됨
              </span>
            </div>
            <button
              onClick={handleViewHighlightedOnMap}
              style={{
                padding: '4px 10px',
                backgroundColor: '#F59E0B',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              지도 보기
            </button>
          </div>
        )}
        
        {/* ==================== Related Companies (Collapsible) ==================== */}
        {/* 🔥 v6.0.2: 템플릿 리터럴 문법 수정 */}
        {!isTyping && !isStreaming && !locationInputMode && relatedCompanies.length > 0 && (
          <div className={`chatbot-companies ${isCompanyListExpanded ? 'expanded' : 'collapsed'}`}>
            <div 
              className="companies-header" 
              onClick={() => setIsCompanyListExpanded(!isCompanyListExpanded)}
            >
              <span className="company-label">
                {i18nService.t('chatbot.relatedCompanies') || '관련 기업 추천'} ({relatedCompanies.length})
              </span>
              <ChevronUp size={16} className="toggle-icon" />
            </div>
            
            <div className="company-list">
              {relatedCompanies.map((comp, idx) => (
                <div 
                  key={comp.id || `company-${idx}`}
                  className="chat-company-card" 
                  onClick={() => handleCompanyClick(comp)}
                >
                  <div className="comp-info">
                    <div className="comp-name">{comp.company_name}</div>
                    <div className="comp-booth">
                      {i18nService.t('chatbot.booth') || '부스'} {comp.booth_number}
                    </div>
                  </div>
                  <div className="comp-action">
                    <Navigation size={14} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* ==================== Input Area ==================== */}
        <div className="chatbot-input-area">
          <input 
            ref={inputRef}
            type="text" 
            placeholder={i18nService.t('chatbot.placeholder') || '메시지를 입력하세요...'}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isTyping || isStreaming || locationInputMode}
          />
          <button 
            className="send-btn" 
            onClick={() => handleSendMessage()}
            disabled={!inputText.trim() || isTyping || isStreaming || locationInputMode}
            title={i18nService.t('chatbot.send') || '전송'}
          >
            <Send size={18} />
          </button>
        </div>

      </div>

      {/* [2026-07-02] 대화 초기화 확인 모달 (window.confirm 대체 — FABLE P1-10) */}
      <ConfirmModal
        isOpen={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
        onConfirm={executeClearChat}
        title={i18nService.t('chatbot.clear_title', {}, '대화 초기화')}
        message={i18nService.t('chatbot.confirmClear') || '대화 내용을 모두 삭제하시겠습니까?'}
        variant="warning"
      />
    </div>
  );
};

export default ChatbotModal;